import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Typography } from "@mui/material";
import PageWrapper from "@components/Home/PageWrapper";
import { gameContinueButtonSx } from "@components/Home/Games/gameUi";

export interface SessionSummaryStats {
  accuracyPercent: number;
  avgReactionSeconds: number;
  xpEarned: number;
  domainChanges: Record<string, number>;
}

export interface SessionSummaryProps {
  stats: SessionSummaryStats;
  onContinue: () => void;
}

const DOMAIN_KEYS: (keyof SessionSummaryStats["domainChanges"])[] = [
  "memory",
  "attention",
  "processing_speed",
  "language",
  "cognitive_flexibility",
  "spatial",
];

const SessionSummary: FC<SessionSummaryProps> = ({ stats, onContinue }) => {
  const { t } = useTranslation();

  return (
    <PageWrapper>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        py={3}
        px={2}
      >
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t("games.sessionSummary.title")}
        </Typography>

        <Box width="100%" maxWidth={320} mt={3}>
          <Typography variant="body1" color="text.secondary">
            {t("games.sessionSummary.accuracy")}:{" "}
            <Box component="span" fontWeight={600} color="text.primary">
              {Math.round(stats.accuracyPercent)}%
            </Box>
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {t("games.sessionSummary.avgReaction")}:{" "}
            <Box component="span" fontWeight={600} color="text.primary">
              {stats.avgReactionSeconds < 1
                ? `${(stats.avgReactionSeconds * 1000).toFixed(0)}ms`
                : `${stats.avgReactionSeconds.toFixed(1)}s`}
            </Box>
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {t("games.sessionSummary.xpEarned")}:{" "}
            <Box component="span" fontWeight={600} color="primary.main">
              +{stats.xpEarned}
            </Box>
          </Typography>
        </Box>

        {(DOMAIN_KEYS.some((k) => (stats.domainChanges[k] ?? 0) !== 0)) && (
          <Box
            width="100%"
            maxWidth={320}
            mt={3}
            py={2}
            px={2}
            sx={{ bgcolor: "action.hover", borderRadius: 2 }}
          >
            {DOMAIN_KEYS.map((key) => {
              const delta = stats.domainChanges[key] ?? 0;
              if (delta === 0) return null;
              const label = t(`games.sessionSummary.domains.${key}`);
              return (
                <Typography key={key} variant="body2" sx={{ mt: 0.5 }}>
                  {label} {delta > 0 ? "↑" : "↓"} {Math.abs(Math.round(delta))}%
                </Typography>
              );
            })}
          </Box>
        )}

        <Button
          variant="contained"
          size="large"
          onClick={onContinue}
          sx={{ ...gameContinueButtonSx, mt: 4 }}
        >
          {t("games.sessionSummary.continue")}
        </Button>
      </Box>
    </PageWrapper>
  );
};

export default SessionSummary;
